import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from "@angular/router";
import { Router } from "@angular/router";
import { StateService } from "./state.service";
import { DomainNavigationService } from "./domain-navigation.service";
import { CatExpertRouterLink } from "../app-routing-enum";
import { Observable, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private stateService: StateService,
    private domainNavigationService: DomainNavigationService
  ) {}

  canActivate(): boolean {
    if (
      !window.localStorage.c ||
      !window.localStorage.i ||
      !window.localStorage.d
    ) {
      return true;
    }

    // Redirect to domain-specific default route
    this.domainNavigationService.navigateToDefaultRoute();
    return false;
  }
}
@Injectable({
  providedIn: "root",
})
export class VerifyAuthGuard implements CanActivate {
  constructor(private router: Router) {}
  canActivate(): boolean {
    if (
      window.localStorage.c ||
      window.localStorage.i ||
      window.localStorage.d
    ) {
      return true;
    }

    this.router.navigateByUrl("/login");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyPHODomain implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService
  ) {}

  canActivate(): boolean {
    if (this.stateService.checkAppConfig() === "pho") {
      return true;
    }

    this.router.navigateByUrl("/studio");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyCatexpertDomain implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService
  ) {}

  canActivate(): boolean {
    if (this.stateService.checkAppConfig() === "catexpert" || "space") {
      return true;
    }

    this.router.navigateByUrl("/studio");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifySpaceDomain implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService
  ) {}

  canActivate(): boolean {
    if (
      this.stateService.checkAppConfig() === "space" ||
      this.stateService.checkAppConfig() === "catexpert"
    ) {
      return true;
    }

    this.router.navigateByUrl("/studio");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyProductLibrary implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService
  ) {}

  canActivate(): boolean {
    // Allow access for Admin, Zone Admin, or Product Library Admin roles
    if (
      this.stateService.hasRole("Admin") ||
      this.stateService.hasZoneAdminAndScope() ||
      this.stateService.hasRole("Product Library Admin")
    ) {
      return true;
    }

    this.router.navigateByUrl("/studio");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyAiImagery implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService
  ) {}

  canActivate(): Observable<boolean> {
    // Check if we already have valid user session data
    const existingUser = this.stateService.getUser();

    if (existingUser && existingUser.id && existingUser.roles) {
      // We have valid session data, check roles directly
      const hasAccess = this.stateService.hasAiImageryAccessRole();
      const hasAdmin = this.stateService.hasAiImageryLibraryAdminRole();

      if (hasAccess || hasAdmin) {
        return of(true);
      }

      this.router.navigateByUrl("/unauthorized/ai-imagery-access");
      return of(false);
    }

    // No valid session data, call the user/me API
    return this.stateService.getUserDetailsAPICall().pipe(
      map((userResponse: any) => {
        // Update user details in state service
        // Handle different possible response structures
        let userData = null;
        if (userResponse) {
          // Check if response has data property
          if (userResponse.data) {
            userData = userResponse.data;
          }
          // Check if response itself is the user data
          else if (
            userResponse.id ||
            userResponse.email ||
            userResponse.roles
          ) {
            userData = userResponse;
          }
        }

        if (userData) {
          this.stateService.setUser(userData);
          localStorage.setItem("user-name", JSON.stringify(userData));
        }

        // Check roles after updating user data
        const hasAccess = this.stateService.hasAiImageryAccessRole();
        const hasAdmin = this.stateService.hasAiImageryLibraryAdminRole();

        // Check if user has either Ai Imagery Access Role or Ai Imagery Library Admin Role
        if (hasAccess || hasAdmin) {
          return true;
        }

        this.router.navigateByUrl("/unauthorized/ai-imagery-access");
        return false;
      }),
      catchError((error) => {
        // On API error, fallback to checking existing user data
        const hasAccess = this.stateService.hasAiImageryAccessRole();
        const hasAdmin = this.stateService.hasAiImageryLibraryAdminRole();

        if (hasAccess || hasAdmin) {
          return of(true);
        }

        this.router.navigateByUrl("/unauthorized/ai-imagery-access");
        return of(false);
      })
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class DomainRouteGuard implements CanActivate {
  constructor(
    private router: Router,
    private stateService: StateService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    const domainType = this.stateService.checkAppConfig();
    const currentPath = state.url.split("/")[1];

    // If accessing home root and domain is ai-imagery, redirect to ai-imagery login screen
    if (currentPath === "home" && domainType === "ai-imagery") {
      this.router.navigateByUrl(`/${CatExpertRouterLink.Login}`);
      return false;
    }

    // If accessing studio root and domain is ai-imagery, redirect to ai-imagery section
    if (currentPath === "studio" && domainType === "ai-imagery") {
      this.router.navigateByUrl(`/${CatExpertRouterLink.AiImagery}`);
      return false;
    }

    // If accessing ai-imagery root and domain is not ai-imagery, redirect to studio
    if (currentPath === "/ai-imagery" && domainType !== "ai-imagery") {
      this.router.navigateByUrl(`/${CatExpertRouterLink.Studio}`);
      return false;
    }

    return true;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyLibraries implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService
  ) {}

  canActivate(): boolean {
    // Allow access for Admin, or Library Admin roles
    if (
      this.stateService.hasRole("Library Admin") ||
      this.stateService.hasRole("Admin")
    ) {
      return true;
    }

    this.router.navigateByUrl(
      `${CatExpertRouterLink.Studio}/${CatExpertRouterLink.Libraries}`
    );
    return false;
  }
}
